import type { TFunction } from 'i18next';

import { useEffect, useState } from 'react';

import {
  type PendingClaims,
  canSaveClaim,
  claimFooter,
  claimPicksOf,
  expenseItemsPreview,
  isClaimDirty,
  isPdfReceiptUrl,
  offlineExpenseService,
  reconcilePendingClaims,
  restLabelKind,
  setClaimUnits,
  shownClaimRows,
  toggleClaim,
  unitsHeld,
  viewerAvailabilities,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { ExpenseLineItem, ExpenseResponseDto } from '@splitfiesta/shared';

import { ImageViewerModal } from '@/components/common/ImageViewerModal';
import { ExpenseReceiptModal } from '@/components/expenses/ExpenseReceiptModal';

import { useClaimLineItemsMutation } from '@/hooks/queries/useClaimLineItems';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { resolveUserName } from '@/lib/displayName';

import { useAuthStore } from '@/stores/authStore';

import { handleApiError } from '@/utils/errorUtils';

import { type LineItemsClaimState, LineItemsSection } from './LineItemsSection';
import { PicksCard, pickMembersOf } from './PicksCard';
import { SplitByItemDialog } from './SplitByItemDialog';

type GroupMember = {
  userId: string | { _id: string; name?: string };
  name?: string;
  isPlaceholder?: boolean;
};

export interface LineItemsSectionContainerProps {
  expense: ExpenseResponseDto & { isOffline?: boolean };
  group: { members?: GroupMember[] } | null | undefined;
}

export function LineItemsSectionContainer({
  expense,
  group,
}: LineItemsSectionContainerProps) {
  const { t } = useTranslation('expenses');
  const user = useAuthStore(state => state.user);
  const { isOnline } = useNetworkStatus();
  const claim = useClaimLineItemsMutation(expense.id, expense.groupId);
  const [pending, setPending] = useState<PendingClaims>({});
  const [error, setError] = useState<string | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const viewerId = user?.id ?? '';
  const items = expense.lineItems;
  const participants = expense.lineItemParticipants ?? [];
  const isParticipant = participants.includes(viewerId);

  const [seenItems, setSeenItems] = useState(items);
  if (items !== seenItems) {
    setSeenItems(items);
    setPending(prev =>
      reconcilePendingClaims(prev, items, viewerId, participants)
    );
  }

  const hidden = !items || offlineExpenseService.isLocalId(expense.id);
  const shown = shownClaimRows(items, pending, viewerId, participants);
  const dirty = !hidden && isClaimDirty(items, shown, viewerId, participants);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers only prompt when returnValue is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (hidden || !items) return null;

  const state: LineItemsClaimState = !isParticipant
    ? 'notParticipant'
    : !isOnline
      ? 'offline'
      : 'editable';

  const memberName = (id: string) => {
    const split = expense.splits.find(s => s.userId === id);
    if (split?.userName) return split.userName;
    const member = group?.members?.find(m =>
      typeof m.userId === 'string' ? m.userId === id : m.userId._id === id
    );
    return typeof member?.userId === 'object'
      ? (member.userId.name ?? '')
      : (member?.name ?? '');
  };
  const nameOf = (id: string) =>
    id === viewerId
      ? t('labels.you')
      : resolveUserName(memberName(id), t as TFunction);
  const rowsOf = (list: ExpenseLineItem[], withPending: boolean) => {
    const { availabilities, hintedId } = viewerAvailabilities(
      list,
      viewerId,
      participants,
      participants,
      withPending ? pending : undefined
    );
    return list.map(item => ({
      item,
      availability: availabilities.get(item.id)!,
      unpickedHint: item.id === hintedId,
    }));
  };

  const previewResult = expenseItemsPreview(expense, shown);
  const { preview, entryCurrency, foreign, rest } = previewResult;

  const restKind = preview
    ? restLabelKind(preview.restAmount, items.length)
    : 'hidden';
  const restRow =
    preview && restKind !== 'hidden'
      ? { kind: restKind, mode: rest.mode, amount: preview.restAmount }
      : null;
  const savedShare =
    expense.splits.find(s => s.userId === viewerId)?.amount ?? 0;
  // Saved picks show the server's share; pending ones show the preview.
  const groupShare = dirty
    ? preview?.splits.find(s => s.userId === viewerId)?.amount
    : savedShare;
  const footer = claimFooter(
    previewResult,
    shown,
    viewerId,
    !dirty && !foreign ? savedShare : undefined
  );
  const total = footer
    ? { amount: footer.total, currency: entryCurrency }
    : { amount: savedShare, currency: expense.currency };

  const asked = !!expense.picksRequestedAt;
  const canSave = canSaveClaim({
    dirty,
    asked,
    viewerPicked: (expense.pickedBy ?? []).some(p => p.userId === viewerId),
  });
  const pickedUnits = items.reduce(
    (n, item) => n + unitsHeld(item, viewerId, participants),
    0
  );

  const frozen = state !== 'editable' || claim.isPending;
  const toggle = (id: string) => {
    if (frozen) return;
    setError(null);
    setPending(prev => toggleClaim(prev, id, items, viewerId, participants));
  };
  const setUnits = (id: string, units: number) => {
    if (frozen) return;
    setError(null);
    setPending(prev =>
      setClaimUnits(prev, id, units, items, viewerId, participants)
    );
  };

  const closeClaim = () => {
    setClaiming(false);
    setPending({});
    setError(null);
  };
  const save = () => {
    if (frozen || !canSave) return;
    setError(null);
    claim.mutate(claimPicksOf(shown, viewerId), {
      onSuccess: closeClaim,
      // Every failure keeps the pending units: the full-set body makes a
      // retry safe, and after a conflict the hook's refetch has already
      // dropped the ones whose units moved underneath them.
      onError: err => {
        setError(handleApiError(err, t as TFunction, 'errors'));
      },
    });
  };

  const receiptUrl = expense.receiptOriginal ?? expense.receipt;

  const openClaim = () => {
    if (state === 'editable') setClaiming(true);
  };

  return (
    <>
      {asked ? (
        <PicksCard
          expense={expense}
          members={pickMembersOf(group?.members)}
          viewerId={viewerId}
          onPickMine={state === 'editable' ? openClaim : undefined}
        />
      ) : null}
      <LineItemsSection
        rows={rowsOf(items, false)}
        nameOf={nameOf}
        currency={entryCurrency}
        rest={restRow}
        state={state}
        pickedUnits={pickedUnits}
        onPick={asked ? undefined : openClaim}
        onViewReceipt={receiptUrl ? () => setReceiptOpen(true) : undefined}
      />
      {isParticipant ? (
        <SplitByItemDialog
          isOpen={claiming}
          onClose={closeClaim}
          claim={{
            rows: rowsOf(shown, true),
            nameOf,
            currency: entryCurrency,
            rest: restRow,
            footer: footer
              ? { itemsAmount: footer.itemsAmount, tip: footer.tipShare }
              : null,
            total,
            groupShare:
              foreign && groupShare !== undefined
                ? { amount: groupShare, currency: expense.currency }
                : undefined,
            canSave,
            dirty,
            saving: claim.isPending,
            disabled: frozen,
            error,
            onToggle: toggle,
            onUnits: setUnits,
            onSave: save,
          }}
        />
      ) : null}
      {receiptUrl && isPdfReceiptUrl(receiptUrl) ? (
        <ExpenseReceiptModal
          isOpen={receiptOpen}
          onClose={() => setReceiptOpen(false)}
          imageUrl={receiptUrl}
          kind="pdf"
        />
      ) : receiptUrl ? (
        <ImageViewerModal
          isOpen={receiptOpen}
          onClose={() => setReceiptOpen(false)}
          imageUrl={receiptUrl}
          title={t('detail.receipt')}
        />
      ) : null}
    </>
  );
}

export default LineItemsSectionContainer;
